import { z } from 'zod'

/**
 * The time zones a venue's clock may be set to: the ones the United States and
 * its territories actually use, and nothing else.
 *
 * A venue's clock is a *place*, because a place is what has daylight saving, and
 * the whole reason this field exists is to decide when an evening actually
 * ended. Narrowing to the zones this business operates in makes the field
 * pickable — 34 options instead of the platform's 418 — and rules out the
 * mistakes below without anybody having to notice them.
 *
 * **Not `America/*`.** The obvious filter is wrong twice over: the prefix has
 * 144 zones including São Paulo and Buenos Aires, and it **misses Hawaii**,
 * which is `Pacific/Honolulu`. Guam, Saipan and Pago Pago are `Pacific/*` too.
 * The list is by country, not by string prefix.
 *
 * **Not the legacy aliases.** `Intl.DateTimeFormat` accepts `EST`, `MST`, `PST`
 * and `GMT`, and they do not behave alike: `EST` and `MST` are fixed offsets
 * that ignore daylight saving, while `PST` happens to observe it in this ICU
 * build. A New York venue set to `EST` would be an hour out for half the year
 * and the value would look perfectly reasonable. None of them are here.
 *
 * The list is pinned rather than computed at run time from
 * `Intl.Locale.prototype.getTimeZones()`, because this module is imported by the
 * browser as well as the API and that method's support is newer than the rest of
 * what we rely on — a missing method would fail at import, which is a worse
 * trade than a list that `venue-clock.spec.ts` checks against the platform on
 * every run.
 *
 * Widening this is one line: add the region to `VENUE_REGIONS` in that spec and
 * paste in what it reports.
 *
 * Validate on write, never on read: a stored name stays perfectly resolvable in
 * `Intl` even if a future ICU renames it, and re-checking old rows would fail
 * them for something nobody did.
 */
const VENUE_ZONES = [
  // United States — mainland
  'America/New_York',
  'America/Detroit',
  'America/Kentucky/Monticello',
  'America/Louisville',
  'America/Indianapolis',
  'America/Indiana/Knox',
  'America/Indiana/Marengo',
  'America/Indiana/Petersburg',
  'America/Indiana/Tell_City',
  'America/Indiana/Vevay',
  'America/Indiana/Vincennes',
  'America/Indiana/Winamac',
  'America/Chicago',
  'America/Menominee',
  'America/North_Dakota/Beulah',
  'America/North_Dakota/Center',
  'America/North_Dakota/New_Salem',
  'America/Denver',
  'America/Boise',
  'America/Phoenix',
  'America/Los_Angeles',
  // Alaska and the Aleutians
  'America/Anchorage',
  'America/Juneau',
  'America/Metlakatla',
  'America/Nome',
  'America/Sitka',
  'America/Yakutat',
  'America/Adak',
  // Hawaii — `Pacific/*`, which is why this is not a prefix filter
  'Pacific/Honolulu',
  // Territories
  'America/Puerto_Rico',
  'America/St_Thomas',
  'Pacific/Guam',
  'Pacific/Saipan',
  'Pacific/Pago_Pago',
] as const

/** A zone a venue may be set to. The allowlist, not every IANA name. */
export type VenueTimezone = (typeof VENUE_ZONES)[number]

const VENUE_ZONE_SET: ReadonlySet<string> = new Set(VENUE_ZONES)

/**
 * A zone a venue may be written with.
 *
 * Trim, then the allowlist. `z.string().refine` would leave the output typed
 * as `string`, so every writer would have to trust the check again. The enum
 * is the check and the type. Reads stay a plain string: a stored name outside
 * this list must still load (see `venueDtoSchema`).
 */
export const timezoneSchema = z
  .string()
  .trim()
  .min(1)
  .max(100)
  .pipe(z.enum(VENUE_ZONES, { error: 'Use a US time zone such as America/Los_Angeles' }))

export function isKnownTimezone(value: string): value is VenueTimezone {
  return VENUE_ZONE_SET.has(value)
}

/**
 * Every zone a venue may use, sorted — the settings form's options.
 *
 * The same set the validator enforces, so the form cannot offer a value the
 * server will refuse. `GET /venues/timezones` serves this rather than the
 * browser building its own list.
 */
export function knownTimezones(): VenueTimezone[] {
  return [...VENUE_ZONES].sort((a, b) => a.localeCompare(b))
}
